/**
 * Текст Пользовательского соглашения (оферты) и метаданные версии.
 *
 * Версия зашита в код и синхронизирована с сид-миграцией 000051 (core.terms_versions).
 * При изменении текста:
 *   1. Увеличить CURRENT_VERSION (семвер: YYYY.MM.patch).
 *   2. Создать новую миграцию с сидом новой версии (is_active=true, старые → false).
 *   3. Обновить PUBLISHED_DATE.
 *   4. Обновить SECTIONS при необходимости.
 *
 * Текст хранится здесь (а не в БД), чтобы страница /terms рендерилась как
 * Server Component без запроса к API. Backend /terms/current возвращает только
 * метаданные версии для проверки актуальности акцепта.
 *
 * Спецификация: SUP-107. Free-only MVP.
 */

export const CURRENT_TERMS_VERSION = '2026.09.1';
export const PUBLISHED_DATE = '2026-09-01';

/**
 * Разделы оферты. Каждый раздел — заголовок + массив параграфов/списков.
 * Рендерится в /terms/page.tsx. Плейсхолдеры {{OOO_NAME}} и др. заполняются
 * после предоставления реквизитов ООО.
 */
export interface TermsSection {
  id: string;
  title: string;
  /** Параграфы текста. */
  paragraphs?: string[];
  /** Списки (маркированные). */
  lists?: string[][];
}

export const TERMS_SECTIONS: TermsSection[] = [
  {
    id: 'definitions',
    title: '1. Термины и определения',
    paragraphs: [
      'Оферта — настоящий документ «Пользовательское соглашение», опубликованный по адресу crosssales.ru/terms.',
      'Акцепт — полное и безоговорочное принятие Оферты пользователем путём регистрации в Сервисе и/или использования Сервиса (ст. 438 ГК РФ).',
      'Сервис — веб-платформа CrossSales (crosssales.ru), предоставляющая функции отслеживания остатков и синхронизации с маркетплейсами.',
      'Пользователь — физическое или юридическое лицо, зарегистрировавшее учётную запись в Сервисе.',
      'Маркетплейс — сторонняя торговая площадка (Ozon, Wildberries, Яндекс.Маркет и др.), с которой Сервис взаимодействует через API.',
      'API-ключ — учётные данные маркетплейса, предоставляемые Пользователем для интеграции.',
      'Оператор — {{OOO_NAME}} (ИНН {{INN}}, ОГРН {{OGRN}}, адрес: {{ADDRESS}}), предоставляющий Сервис.',
    ],
  },
  {
    id: 'general',
    title: '2. Общие положения',
    paragraphs: [
      'Оферта является публичной (ст. 437 ГК РФ). Акцепт Оферты осуществляется путём регистрации в Сервисе и/или начала использования Сервиса.',
      'Акцепт создаёт договор между Пользователем и Оператором на условиях Оферты (ст. 438 ГК РФ).',
      'Использование Сервиса без акцепта Оферты не допускается.',
    ],
  },
  {
    id: 'registration',
    title: '3. Регистрация и учётная запись',
    lists: [
      [
        'Для использования Сервиса необходимо зарегистрировать учётную запись, указав достоверные данные.',
        'Одна учётная запись предназначена для одного Пользователя. Передача учётной записи третьим лицам запрещена.',
        'Пользователь несёт ответственность за сохранность данных для входа и за все действия, совершённые с использованием его учётной записи.',
        'На одну организацию (тенант) в рамках бесплатного тарифа создаётся одна учётная запись пользователя-владельца.',
      ],
    ],
  },
  {
    id: 'service-description',
    title: '4. Описание Сервиса и тариф',
    paragraphs: [
      'Сервис предоставляет Пользователю следующие функции в рамках бесплатного тарифа (Free):',
    ],
    lists: [
      [
        'Отслеживание остатков товаров на собственных складах.',
        'Синхронизация остатков с подключёнными маркетплейсами.',
        'Одно подключение маркетплейса на одну организацию (тенант).',
        'Хранение до 50 товарных позиций (SKU).',
        'Импорт товаров из маркетплейсов.',
      ],
      [
        'Создание карточек товаров на маркетплейсах — недоступно в текущей редакции.',
        'Платные тарифы — недоступны в текущей редакции.',
      ],
    ],
  },
  {
    id: 'changes',
    title: '5. Возможные изменения функционала и тарифов',
    paragraphs: [
      'Оператор вправе изменять состав функций Сервиса, вводить новые функции, изменять ограничения (в т.ч. количество SKU, подключений маркетплейсов, пользователей на тенант), а также вводить новые тарифы и изменять цены на существующие тарифы.',
      'О любых существенных изменениях Оператор уведомляет Пользователя по электронной почте и/или посредством баннера в интерфейсе Сервиса не менее чем за 14 календарных дней до вступления изменений в силу.',
      'Продолжение использования Сервиса после истечения указанного срока означает согласие Пользователя с изменениями. При несогласии Пользователь вправе прекратить использование Сервиса и удалить учётную запись.',
    ],
  },
  {
    id: 'api-keys',
    title: '6. API-ключи маркетплейсов',
    lists: [
      [
        'Пользователь подтверждает, что имеет право на использование предоставляемых API-ключей маркетплейсов.',
        'Пользователь обязуется соблюдать условия использования соответствующих маркетплейсов.',
        'Оператор не несёт ответственности за действия, выполненные маркетплейсами на основании данных, переданных Пользователем.',
        'Пользователь несёт ответственность за утечку API-ключей по своей вине и обязуется незамедлительно уведомить Оператора о компрометации ключей.',
        'Оператор вправе удалить API-ключи по запросу маркетплейса или при выявлении несанкционированного использования.',
      ],
    ],
  },
  {
    id: 'personal-data',
    title: '7. Обработка персональных данных',
    paragraphs: [
      'Обработка персональных данных Пользователя осуществляется в соответствии с Политикой конфиденциальности, опубликованной по адресу crosssales.ru/privacy, и законодательством РФ (152-ФЗ).',
      'При регистрации Пользователь даёт согласие на обработку персональных данных в целях регистрации и оказания услуг (ст. 9 152-ФЗ).',
      'Пользователь вправе отозвать согласие и удалить свои персональные данные через интерфейс Сервиса. Отзыв согласия влечёт прекращение оказания услуг и удаление учётной записи.',
      'При использовании файлов cookie Пользователь даёт согласие на их использование в соответствии с баннером cookie.',
    ],
  },
  {
    id: 'cookies',
    title: '8. Файлы cookie',
    paragraphs: [
      'Сервис использует файлы cookie для обеспечения работоспособности и аналитики. При первом посещении Пользователю отображается баннер cookie с возможностью принятия или отказа.',
      'Политика использования cookie описана в Политике конфиденциальности (crosssales.ru/privacy).',
    ],
  },
  {
    id: 'aup',
    title: '9. Правила использования',
    paragraphs: ['При использовании Сервиса запрещается:'],
    lists: [
      [
        'Нарушать законодательство Российской Федерации.',
        'Передавать данные для входа третьим лицам.',
        'Пытаться получить несанкционированный доступ к Сервису или его инфраструктуре.',
        'Использовать Сервис для распространения вредоносного ПО или незаконного контента.',
        'Создавать чрезмерную нагрузку на инфраструктуру Сервиса, включая автоматизированный парсинг, скрейпинг, reverse engineering и нагрузочное тестирование без письменного разрешения Оператора.',
        'Использовать автоматизированные средства (боты, скрипты) для доступа к Сервису иначе как через официально предоставляемый API (если применимо).',
      ],
    ],
  },
  {
    id: 'ip',
    title: '10. Интеллектуальная собственность и индемнитет',
    paragraphs: [
      'Все права на Сервис, включая программный код, дизайн, логотипы и документацию, принадлежат Оператору. Использование материалов Сервиса без разрешения запрещено.',
      'Пользователь заверяет, что обладает всеми необходимыми правами на контент (товарные карточки, изображения, описания), загружаемый в Сервис. Пользователь обязуется возместить Оператору убытки, возникшие в связи с претензиями третьих лиц к нарушению их прав контентом Пользователя.',
    ],
  },
  {
    id: 'liability',
    title: '11. Ограничение ответственности',
    paragraphs: [
      'Сервис предоставляется на безвозмездной основе («как есть»). Оператор не гарантирует бесперебойную работу Сервиса, отсутствие ошибок и совместимость со всеми версиями API маркетплейсов.',
      'Поскольку Сервис предоставляется бесплатно, ответственность Оператора перед Пользователем ограничена суммой, фактически уплаченной Пользователем за использование Сервиса (то есть нулём), за исключением убытков, причинённых умышленно или грубой неосторожностью Оператора (ст. 402.1 ГК РФ).',
      'Оператор не несёт ответственности за убытки, возникшие в результате ошибок синхронизации данных с маркетплейсами, изменений в API маркетплейсов, действий третьих лиц и обстоятельств непреодолимой силы.',
    ],
  },
  {
    id: 'amendments',
    title: '12. Изменение условий Оферты',
    paragraphs: [
      'Оператор вправе изменять Оферту. О существенных изменениях Оператор уведомляет Пользователя по электронной почте и/или посредством баннера в интерфейсе не менее чем за 14 календарных дней до вступления изменений в силу (ст. 452 ГК РФ).',
      'Продолжение использования Сервиса после истечения указанного срока означает акцепт новой редакции Оферты. При несогласии Пользователь вправе прекратить использование Сервиса и удалить учётную запись.',
    ],
  },
  {
    id: 'termination',
    title: '13. Прекращение действия',
    paragraphs: [
      'Оператор вправе приостановить или прекратить доступ Пользователя к Сервису в случае нарушения Оферты, в том числе нарушения правил использования (раздел 9).',
      'Пользователь вправе удалить учётную запись в любое время через интерфейс Сервиса. Удаление учётной записи влечёт удаление связанных данных в соответствии с Политикой конфиденциальности.',
      'Отзыв согласия на обработку персональных данных (раздел 7) влечёт прекращение оказания услуг и удаление учётной записи.',
    ],
  },
  {
    id: 'law',
    title: '14. Применимое право и подсудность',
    paragraphs: [
      'Оферта и отношения между Оператором и Пользователем регулируются законодательством Российской Федерации.',
      'Все споры подлежат разрешению в Арбитражном суде Российской Федерации по месту нахождения Оператора (ст. 35 АПК РФ).',
    ],
  },
  {
    id: 'requisites',
    title: '15. Реквизиты Оператора',
    lists: [
      [
        'Наименование: {{OOO_NAME}}',
        'ИНН: {{INN}}',
        'ОГРН: {{OGRN}}',
        'Адрес: {{ADDRESS}}',
        'Расчётный счёт: {{BANK_ACCOUNT}}',
        'Банк: {{BANK_NAME}}',
        'Корр. счёт: {{BANK_KORR}}',
        'БИК: {{BANK_BIK}}',
      ],
    ],
  },
  {
    id: 'contacts',
    title: '16. Контакты',
    paragraphs: [
      'По вопросам, связанным с использованием Сервиса и настоящей Офертой, обращайтесь по адресу: support@crossales.ru.',
    ],
  },
];
